import React, { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlanChangeLink } from '../../utils/constant'
import './featureVideoCallout.css'

/**
 * Tracks whether a callout has been dismissed, persisted per-key in localStorage
 * so it survives reloads but stays local to the device (same pattern as the old
 * recording tooltip and the words-discovery callout).
 *
 * `autoOpen` (default true) controls whether the callout shows itself on first
 * render. Pass `autoOpen: false` for audiences that should only see it after an
 * explicit interaction (e.g. Standard learners clicking a locked feature) — then
 * trigger it with the returned `open`.
 */
export const useFeatureVideoCallout = (storageKey: string, options?: { autoOpen?: boolean }) => {
  const autoOpen = options?.autoOpen ?? true
  const [isOpen, setIsOpen] = useState(() => {
    if (!autoOpen) return false
    try {
      return localStorage.getItem(storageKey) !== 'true'
    } catch {
      return false
    }
  })

  const dismiss = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.stopPropagation()
      setIsOpen(false)
      try {
        localStorage.setItem(storageKey, 'true')
      } catch {
        /* storage unavailable — dismiss for this session only */
      }
    },
    [storageKey],
  )

  const open = useCallback((e?: React.SyntheticEvent) => {
    e?.stopPropagation()
    setIsOpen(true)
  }, [])

  return { isOpen, dismiss, open }
}

interface FeatureVideoCalloutProps {
  title: string
  description: string
  /** A plain line under the description, e.g. the teacher-language notice on Pro upsells. */
  note?: string
  videoSrc: string
  videoLabel: string
  onDismiss: (e?: React.SyntheticEvent) => void
  /** Upsell mode for Free/Standard learners: shows a Pro badge and an upgrade CTA. */
  isLockedFeature?: boolean
  ctaText?: string
  /** Which side the arrow sits on (i.e. where the anchor element is). */
  arrowAlign?: 'left' | 'right'
}

/**
 * Anchored, dismissible callout with an embedded click-to-play video.
 * Render it inside a `position: relative` wrapper around the element it points at.
 *
 * The video follows the same click-to-play pattern as the plan page demos:
 * poster frame + overlay play button, native controls only once playing
 * (Safari/iOS block autoplay with sound, and we don't want sound uninvited).
 */
const FeatureVideoCallout: React.FC<FeatureVideoCalloutProps> = ({
  title,
  description,
  note,
  videoSrc,
  videoLabel,
  onDismiss,
  isLockedFeature = false,
  ctaText,
  arrowAlign = 'right',
}) => {
  // No Suspense: the callout opens over a page that is already showing, and suspending would blank
  // that page while the namespace loads.
  const { t, ready } = useTranslation('dashboard', { useSuspense: false })
  const navigate = useNavigate()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  // Safari won't paint the first frame as a preview with preload='metadata' alone —
  // the #t=0.1 media fragment forces it to seek to 0.1s so that frame shows.
  const previewSrc = videoSrc.includes('#') ? videoSrc : `${videoSrc}#t=0.1`

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    const v = videoRef.current
    if (!v) return
    try {
      v.currentTime = 0
    } catch {
      /* ignore */
    }
    v.play()
      .then(() => setIsPlaying(true))
      .catch(() => {
        // Play rejected — still show controls so the user can retry
        setIsPlaying(true)
      })
  }

  const handleUpgrade = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(PlanChangeLink)
  }

  if (!ready) return null

  return (
    <div
      className={`feature-video-callout feature-video-callout--arrow-${arrowAlign}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button className='feature-video-callout__close' onClick={onDismiss} aria-label={t('videoCallout.dismiss')}>
        <i className='fa fa-times'></i>
      </button>
      <div className='feature-video-callout__header'>
        <strong>{title}</strong>
        {isLockedFeature && <span className='feature-video-callout__pro-badge'>{t('videoCallout.pro')}</span>}
      </div>
      <span className='feature-video-callout__description'>{description}</span>
      {note && <span className='feature-video-callout__description'>{note}</span>}
      <div className={`feature-video-callout__video${isPlaying ? ' is-playing' : ''}`}>
        <video
          ref={videoRef}
          className='feature-video-callout__video-media'
          src={previewSrc}
          playsInline
          preload='metadata'
          controls={isPlaying}
          onEnded={() => setIsPlaying(false)}
        />
        {!isPlaying && (
          <button type='button' className='feature-video-callout__video-play' onClick={handlePlay} aria-label={videoLabel}>
            <span className='feature-video-callout__video-play-icon' aria-hidden='true'>
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <path d='M8 5v14l11-7L8 5z' fill='currentColor' />
              </svg>
            </span>
            <span className='feature-video-callout__video-play-label'>{videoLabel}</span>
          </button>
        )}
      </div>
      {isLockedFeature && (
        <button type='button' className='feature-video-callout__cta' onClick={handleUpgrade}>
          {ctaText ?? t('videoCallout.upgrade')}
          <span className='feature-video-callout__cta-arrow'>›</span>
        </button>
      )}
      <div className='feature-video-callout__arrow'></div>
    </div>
  )
}

export default FeatureVideoCallout
